import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, TextAreaField } from './charte';
import { Sheet } from './shell/Sheet';
import { StarRating } from './StarRating';
import { dismissInstructorRating, rateInstructor } from '../api/candidate';
import { slotLabel } from '../lib/format';
import {
  RATING_COMMENT_MAX,
  emptyRating,
  ratingPayload,
  ratingReady,
  scoreLabel,
  scoreWord,
  type LessonToRate,
  type RatingCriterion,
  type RatingDraft,
} from '../lib/instructorRating';

export const PENDING_RATINGS_KEY = ['candidate', 'instructor-ratings', 'pending'];

const errorText = (e: unknown, fallback: string) => {
  const message = (e as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  return typeof message === 'string' && message ? message : fallback;
};

/**
 * Notation du moniteur après une leçon émargée : étoiles obligatoires (demi-étoiles acceptées),
 * critères et commentaire facultatifs. « Plus tard » ferme sans rien envoyer ; « Ne pas noter »
 * retire la leçon des propositions.
 */
export function InstructorRatingSheet({
  lesson,
  criteria,
  remaining,
  onClose,
}: {
  lesson: LessonToRate;
  criteria: RatingCriterion[];
  /** Autres leçons à noter après celle-ci. */
  remaining: number;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<RatingDraft>(emptyRating);
  const [done, setDone] = useState(false);
  const instructor = lesson.instructorName || 'votre moniteur';
  const refresh = () => queryClient.invalidateQueries({ queryKey: PENDING_RATINGS_KEY });

  const send = useMutation({
    mutationFn: () => rateInstructor(lesson.lessonId, ratingPayload(draft)),
    onSuccess: () => setDone(true),
  });
  const skip = useMutation({
    mutationFn: () => dismissInstructorRating(lesson.lessonId),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  const finish = async () => {
    await refresh();
    onClose();
  };
  // La leçon notée sort de la liste : la fenêtre passe d'elle-même à la suivante (nouvelle clé).
  const next = () => void refresh();
  const busy = send.isPending || skip.isPending;

  if (done)
    return (
      <Sheet
        title="Merci pour votre avis !"
        description={`Votre note aide ${instructor} et votre auto-école à progresser.`}
        onClose={finish}
        footer={
          <Button variant="primary" className="flex-1" onClick={remaining > 0 ? next : finish}>
            {remaining > 0 ? 'Noter la leçon suivante' : 'Fermer'}
          </Button>
        }
      >
        <p role="status" className="flex items-center gap-3 rounded-row bg-success-light px-4 py-3 text-sm text-success-dark">
          Note envoyée : {scoreLabel(draft.score ?? 0)} / 5
        </p>
      </Sheet>
    );

  return (
    <Sheet
      title={`Comment s’est passée votre leçon avec ${instructor} ?`}
      description={slotLabel(lesson.startsAt, lesson.endsAt)}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" disabled={busy} loading={skip.isPending} onClick={() => skip.mutate()}>
            Ne pas noter
          </Button>
          <Button
            variant="primary"
            className="flex-1"
            disabled={!ratingReady(draft) || busy}
            loading={send.isPending}
            onClick={() => send.mutate()}
          >
            Envoyer ma note
          </Button>
        </>
      }
    >
      <div className="flex flex-col items-center gap-2 py-2 text-center">
        <StarRating
          label="Note de la leçon"
          value={draft.score}
          onChange={(score) => setDraft((d) => ({ ...d, score }))}
        />
        <p className="text-sm font-semibold text-heading" aria-live="polite">
          {draft.score !== null && <span className="mr-1.5">{scoreLabel(draft.score)} / 5 ·</span>}
          {scoreWord(draft.score)}
        </p>
      </div>

      {criteria.length > 0 && (
        <fieldset className="mt-4 border-t border-slate-100 pt-4">
          <legend className="text-sm font-medium text-heading">
            Détaillez si vous le souhaitez <span className="font-normal text-secondary">(facultatif)</span>
          </legend>
          <ul className="mt-2 grid gap-2.5">
            {criteria.map((criterion) => (
              <li key={criterion.key} className="flex items-center justify-between gap-3">
                <span className="text-[13px] text-body">{criterion.label}</span>
                <StarRating
                  size="sm"
                  clearable
                  label={criterion.label}
                  value={draft.criteria[criterion.key] ?? null}
                  onChange={(score) =>
                    setDraft((d) => {
                      const next = { ...d.criteria };
                      if (score === null) delete next[criterion.key];
                      else next[criterion.key] = score;
                      return { ...d, criteria: next };
                    })
                  }
                />
              </li>
            ))}
          </ul>
        </fieldset>
      )}

      <div className="mt-4 border-t border-slate-100 pt-4">
        <TextAreaField
          label="Un commentaire ? (facultatif)"
          maxLength={RATING_COMMENT_MAX}
          value={draft.comment}
          onChange={(e) => setDraft((d) => ({ ...d, comment: e.target.value }))}
          hint={`${draft.comment.length} / ${RATING_COMMENT_MAX}`}
        />
      </div>

      {(send.isError || skip.isError) && (
        <p role="alert" className="mt-3 rounded-row bg-danger-light px-4 py-3 text-sm text-danger-dark">
          {errorText(send.error ?? skip.error, 'Envoi impossible pour le moment. Réessayez.')}
        </p>
      )}
    </Sheet>
  );
}
